import { Button } from '@fountain/ripple/button';
import { ButtonIcon } from '@fountain/ripple/button-icon';
import { Select } from '@fountain/ripple/select';
import { Text } from '@fountain/ripple/text';
import { TextField } from '@fountain/ripple/text-field';
import { PlusOutlineIcon, TrashOutlineIcon } from '@fountain/ripple/icon';
import { makeStyles } from '@material-ui/core';
import React, { useId } from 'react';
import { useIntl } from 'react-intl';

import { messages } from './ReportMappings.messages';

// Display draft only: the host adapts employeeNumberColumn to its merged save contract.
// The generic preserves canonical columns and all other report fields without owning their schema.
export interface ReportMapping {
  key: string;
  name: string;
  employeeNumberColumn?: string;
}

export interface ReportDiscoveredColumns {
  reportKey: string;
  // Returned, purpose-qualified employee-number options from the metadata owner.
  columnNames: string[];
}

export interface ReportMappingError {
  path: string;
  code: 'missing_column' | 'required_filter' | 'missing_applicant_field';
}

export interface ReportMappingsProps<T extends ReportMapping> {
  unavailableReason?: string;
  reports: T[];
  discoveredColumns: ReportDiscoveredColumns[];
  fieldErrors: ReportMappingError[];
  onChange: (reports: T[]) => void;
}

export interface ReportFilter {
  key: string;
  field: string;
  name: string;
  editable?: boolean;
}

export interface ReportFiltersProps {
  filters: ReportFilter[];
  applicantFields: { value: string; label: string; disabled?: boolean }[];
  fieldErrors: ReportMappingError[];
  onFiltersChange: (filters: ReportFilter[]) => void;
  onAdd: () => void;
  matchingHelp?: React.ReactNode;
}

const useStyles = makeStyles(theme => ({
  section: { minWidth: 0, border: 0, padding: 0, margin: 0 },
  title: { margin: 0 },
  help: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 300px) 16px minmax(0, 268px) 32px',
    gap: theme.spacing(1, 1.5),
    alignItems: 'start',
    marginTop: theme.spacing(1.5),
  },
  outputs: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 220px) minmax(0, 268px)',
    gap: theme.spacing(1, 1.5),
    alignItems: 'start',
    marginTop: theme.spacing(1),
  },
  value: { minWidth: 0, overflowWrap: 'anywhere' },
  equal: { paddingTop: theme.spacing(1), textAlign: 'center' },
  add: { marginTop: theme.spacing(1) },
}));

const ERROR_MESSAGES = {
  missing_column: messages.missingColumn,
  required_filter: messages.requiredFilter,
  missing_applicant_field: messages.missingApplicantField,
};

// Compose Select so the drawn column headings need no duplicate per-row visible label.
function MappingSelect({
  name,
  items,
  value,
  error,
  disabled,
  placeholder,
  onChange,
}: {
  name: string;
  items: { value: string; label: string; disabled?: boolean }[];
  value: string;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  const errorId = useId();
  return (
    <div>
      <Select.Root
        items={items}
        value={value}
        onValueChange={value => onChange(value ?? '')}
        disabled={disabled}
      >
        <Select.Trigger
          aria-label={name}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          invalid={Boolean(error)}
          placeholder={placeholder}
        />
        <Select.Content>
          {items.map(item => (
            <Select.Item
              key={item.value}
              value={item.value}
              disabled={item.disabled}
            >
              {item.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
      {error && (
        <div role="alert">
          <Select.FieldFeedback id={errorId}>{error}</Select.FieldFeedback>
        </div>
      )}
    </div>
  );
}

/** Shared report filters. The host owns IDs, draft changes and the matching-help popover. */
export function ReportFilters({
  filters,
  applicantFields,
  fieldErrors,
  onFiltersChange,
  onAdd,
  matchingHelp,
}: ReportFiltersProps) {
  const intl = useIntl();
  const styles = useStyles();
  const titleId = useId();
  const error = (path: string) => {
    const item = fieldErrors.find(item => item.path === path);
    return item ? intl.formatMessage(ERROR_MESSAGES[item.code]) : undefined;
  };
  const update = (key: string, patch: Partial<ReportFilter>) =>
    onFiltersChange(
      filters.map(filter =>
        filter.key === key ? { ...filter, ...patch } : filter,
      ),
    );

  return (
    <fieldset className={styles.section} aria-labelledby={titleId}>
      <h4 id={titleId} className={styles.title}>
        <Text variant="body2" render={<span />}>
          {intl.formatMessage(messages.search)}
        </Text>
      </h4>
      <div className={styles.help}>
        <Text variant="subtitle1">
          {intl.formatMessage(messages.searchHelp)}
        </Text>
        {matchingHelp}
      </div>
      <div className={styles.filters}>
        <Text variant="small1">
          {intl.formatMessage(messages.applicantHeading)}
        </Text>
        <span />
        <div>
          <Text variant="small1">
            {intl.formatMessage(messages.filterHeading)}
          </Text>
          <Text variant="small1">
            {intl.formatMessage(messages.filterHelp)}
          </Text>
        </div>
        <span />
        {filters.map((filter, index) => {
          const field = applicantFields.find(
            option => option.value === filter.field,
          );
          const label =
            field?.label ??
            intl.formatMessage(messages.value, { number: index + 1 });
          const fieldError = error(`filters.${filter.key}.field`);
          return (
            <React.Fragment key={filter.key}>
              {filter.editable || !field ? (
                <MappingSelect
                  name={intl.formatMessage(messages.applicantField, {
                    number: index + 1,
                  })}
                  items={applicantFields}
                  value={filter.field}
                  error={fieldError}
                  onChange={value => update(filter.key, { field: value })}
                />
              ) : (
                <Text variant="body1" className={styles.value}>
                  {label}
                </Text>
              )}
              <span className={styles.equal} aria-hidden="true">
                =
              </span>
              <TextField
                size="sm"
                aria-label={intl.formatMessage(messages.reportFilter, {
                  field: label,
                })}
                value={filter.name}
                error={error(`filters.${filter.key}.name`)}
                onChange={event =>
                  update(filter.key, { name: event.target.value })
                }
              />
              <ButtonIcon
                variant="text-neutral"
                size="sm"
                icon={TrashOutlineIcon}
                type="button"
                aria-label={intl.formatMessage(messages.removeValue, {
                  field: label,
                })}
                onClick={() =>
                  onFiltersChange(
                    filters.filter(item => item.key !== filter.key),
                  )
                }
              />
            </React.Fragment>
          );
        })}
      </div>
      <Button
        type="button"
        variant="text-brand"
        size="sm"
        startIcon={PlusOutlineIcon}
        className={styles.add}
        onClick={onAdd}
      >
        {intl.formatMessage(messages.addValue)}
      </Button>
    </fieldset>
  );
}

/** Per-report employee numbers are never substituted for canonical person references. */
export function ReportMappings<T extends ReportMapping>({
  reports,
  discoveredColumns,
  fieldErrors,
  onChange,
  unavailableReason,
}: ReportMappingsProps<T>) {
  const intl = useIntl();
  const styles = useStyles();
  const titleId = useId();

  if (unavailableReason) return <Text role="status">{unavailableReason}</Text>;

  return (
    <section className={styles.section} aria-labelledby={titleId}>
      <h4 id={titleId} className={styles.title}>
        <Text variant="body2" render={<span />}>
          {intl.formatMessage(messages.employeeId)}
        </Text>{' '}
        <Text variant="subtitle1" render={<span />}>
          → rehire_check_matched_employee_number
        </Text>
      </h4>
      <div className={styles.outputs}>
        <Text variant="small1">
          {intl.formatMessage(messages.reportHeading)}
        </Text>
        <Text variant="small1">
          {intl.formatMessage(messages.columnHeading)}
        </Text>
        {reports.map(report => {
          const metadata = discoveredColumns.find(
            item => item.reportKey === report.key,
          );
          const allowed = metadata?.columnNames ?? [];
          const selected = report.employeeNumberColumn ?? '';
          const missing = Boolean(
            metadata && selected && !allowed.includes(selected),
          );
          const fieldError = fieldErrors.find(
            item => item.path === `reports.${report.key}.employeeNumberColumn`,
          );
          const error =
            (fieldError
              ? intl.formatMessage(ERROR_MESSAGES[fieldError.code])
              : undefined) ??
            (missing ? intl.formatMessage(messages.missingColumn) : undefined);
          const items = allowed.map(column => ({
            value: column,
            label: column,
            disabled: false,
          }));
          if (selected && !allowed.includes(selected))
            items.push({ value: selected, label: selected, disabled: true });
          return (
            <React.Fragment key={report.key}>
              <Text variant="body1" className={styles.value}>
                {report.name}
              </Text>
              <MappingSelect
                name={intl.formatMessage(messages.employeeColumn, {
                  report: report.name,
                })}
                items={items}
                value={selected}
                disabled={!metadata}
                placeholder={intl.formatMessage(
                  metadata ? messages.chooseColumn : messages.runFirst,
                )}
                error={error}
                onChange={value => {
                  if (!allowed.includes(value)) return;
                  onChange(
                    reports.map(item =>
                      item.key === report.key
                        ? { ...item, employeeNumberColumn: value }
                        : item,
                    ),
                  );
                }}
              />
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}
